// @flow

import { put, call, all, takeEvery, select } from "redux-saga/effects";
import type { Action } from "redux-action";

import http from "@http";
import { isFirstCareUser } from "@modules/account/selectors";
import { sendSuccessMessage } from "@modules/app/actionCreator";
import * as actions from "@modules/service/constants/actions";
import actionCreator from "@modules/service/actionCreator";
import settingsActionCreator from "@modules/settings/actionCreator";
import type {
  ServiceSettingsContainer,
  ServiceSettings,
} from "@modules/service/reducer/service-settings/types";
import { getSchema } from "@utils/schema";

type GetServiceSettingsRequest = $Exact<{
  clientId: string,
}>;

export function* onGetServiceSettings({
  payload,
}: Action<GetServiceSettingsRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/service-settings`,
    );

    yield put(
      actionCreator.GetServiceSettingsSuccess({
        active: response.data,
      }),
    );
  } catch (e) {
    yield put(actionCreator.GetServiceSettingsError((e || {}).response));
  }
}

export function* onGetServiceSettingsSchema(): Generator<*, void, ServiceSettingsContainer> {
  try {
    const schema = yield getSchema("ServiceSettingsServiceSectionDTO");

    yield put(actionCreator.GetServiceSettingsSchemaSuccess(schema));
  } catch (e) {
    yield put(actionCreator.GetServiceSettingsSchemaError((e || {}).response));
  }
}

type UpdateServiceSettingsRequest = $Exact<{
  clientId: string,
  settings: ServiceSettings,
}>;

export function* onUpdateServiceSettings({
  payload,
}: Action<UpdateServiceSettingsRequest>): Generator<*, void, ServiceSettings> {
  try {
    if (!payload || !payload.clientId || !payload.settings) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    yield call(
      http.put,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/service-settings`,
      payload.settings,
    );

    yield all([
      put(actionCreator.UpdateServiceSettingsSuccess()),
      put(actionCreator.GetServiceSettings({ clientId: payload.clientId })),
      put(sendSuccessMessage("SERVICE_SETTINGS_UPDATED")),
      put(settingsActionCreator.GetSettings(payload)),
    ]);
  } catch (e) {
    yield put(actionCreator.UpdateServiceSettingsError((e || {}).response));
  }
}

// ROOT

export function* ServiceSettingsSagas(): Generator<*, void, void> {
  yield all([
    takeEvery(actions.GetServiceSettings, onGetServiceSettings),
    takeEvery(actions.GetServiceSettingsSchema, onGetServiceSettingsSchema),
    takeEvery(actions.UpdateServiceSettings, onUpdateServiceSettings),
  ]);
}

export default ServiceSettingsSagas;
